import React, { useEffect, useState } from "react";
import Link from "components/ExtendLink";
import {
  Button,
  CustomInput,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import Select from "react-select";
import RequiredSelect from "./RequiredSelect";
import { useFetch2 } from "components/utils/authenticatedHook";
import _ from "lodash";
import { ExtremeBloodPressureReadingMessage, ExtremeGlucoseReadingMessage, ExtremeWeightReadingMessage, ExtremeOxigenReadingMessage } from "../../constants/general.constant";
import { withRouter } from "react-router-dom";

function AddExtremeReading(props) {
  const history = props.history;
  const [{ response, error, isLoading }, doFetch, sendError] = useFetch2();
  const [
    {
      response: createExteremeResponse,
      error: createExtremeError,
      isLoading: isCreating,
    },
    doFetchCreate,
    sendCreateError,
  ] = useFetch2();
  const [formData, setFormData] = useState({
    name: "",
    readingType: null,
    readingSubType: [],
    condition: null,
    analyzeBy: null,
    minValue: "",
    maxValue: "",
    value: "",
    gainLossPerDay: "",
    notifyReadingOwner: [],
    notifySelf: [],
    notifyStaff: [],
    patients: [],
    extremeReadingCareProviderMessage: "",
    extremeReadingPatientMessage: "",
    extremeReadingOneHourCareProviderMessage: "",
  });
  const [formError, setFormError] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    doFetch({
      url: "/get-extreme-reading-type-and-condition",
      method: "GET",
    });
  }, []);

  useEffect(() => {
    if (
      response?.data?.readingType &&
      response.data.readingType.length > 0 &&
      !formData.readingType // only set if not already selected
    ) {
      const firstOption = response.data.readingType[0];
      const messages = defaultMessageSet(firstOption);
      setFormData((prev) => ({
        ...prev,
        readingType: firstOption,
        extremeReadingCareProviderMessage: messages.extremeReadingCareProvider,
        extremeReadingPatientMessage: messages.extremeReadingPatient,
        extremeReadingOneHourCareProviderMessage: messages.extremeReadingOneHourCareProvider
      }));
    }
  }, [response?.data?.readingType]);

  const analyzeByValue =
    typeof formData.analyzeBy === "object" && formData.analyzeBy
      ? formData.analyzeBy.value
      : formData.analyzeBy;

  // Get selected subtypes (array)
  const selectedSubTypes = Array.isArray(formData.readingSubType)
    ? formData.readingSubType
    : [];

  // Validation function
  const validateForm = (data = formData) => {
    const errors = {};
    if (!data.name || !data.name.trim()) errors.name = "Name is required";
    if (!data.readingType) errors.readingType = "Reading type is required";
    if (!data.readingSubType || data.readingSubType.length === 0)
      errors.readingSubType = "Reading Sub Type is required";
    if (!data.analyzeBy && data.readingType && data.readingType._id !== "5bb306382598931ffbd1b625") errors.analyzeBy = "Analyze type is required";
    // Validate analyze value for each selected sub type
    if (selectedSubTypes.length > 0) {
      selectedSubTypes.forEach((sub) => {
        if(data.readingType && data.readingType._id == "5bb306382598931ffbd1b625"){
          if (!data[`gainLossPerDay_${sub._id}`]) {
              errors[`analyzeValue_${sub._id}`] = "Weight is required";
          }
        }else{
          if (analyzeByValue === "between") {
            const min = data[`minValue_${sub._id}`];
            const max = data[`maxValue_${sub._id}`];

            if (!min || !max) {
              errors[`analyzeValue_${sub._id}`] = "Both min and max values are required";
            } else if (parseFloat(max) <= parseFloat(min)) {
              errors[`analyzeValue_${sub._id}`] = "Max value must be greater than min value";
            }

          } else {
            if (!data[`value_${sub._id}`]) {
              errors[`analyzeValue_${sub._id}`] = "Value is required";
            }
          }
        }
      });
    }

    if (
      (!data.patients || data.patients.length === 0) &&
      (!data.condition || data.condition.length === 0)
    ) {
      errors.patients = "Monitor patient is required";
    }
    // Check all three messages are required
    if (
      !data.extremeReadingCareProviderMessage ||
      !data.extremeReadingCareProviderMessage.trim()
    ) {
      errors.extremeReadingCareProviderMessage =
        "Care Provider message is required";
    }
    if (
      !data.extremeReadingPatientMessage ||
      !data.extremeReadingPatientMessage.trim()
    ) {
      errors.extremeReadingPatientMessage = "Patient message is required";
    }
    if (
      !data.extremeReadingOneHourCareProviderMessage ||
      !data.extremeReadingOneHourCareProviderMessage.trim()
    ) {
      errors.extremeReadingOneHourCareProviderMessage =
        "One Hour Care Provider message is required";
    }
    // Check notification arrays are required and not empty
    if (!data.notifyReadingOwner || data.notifyReadingOwner.length === 0) {
      errors.notifyReadingOwner =
        "At least one notification method for Reading Owner is required";
    }
     if (!data.notifyStaff || data.notifyStaff.length === 0) {
      errors.notifyStaff =
        "At least one notification method for Self is required";
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errors = validateForm();
    setFormError(errors);
    if (Object.keys(errors).length === 0) {
      const subTypeValues = selectedSubTypes.map((sub) => ({
        name: sub.name == "Beats Per Minute(BPM)" ? "Heart Rate" : sub.name,
        readingSubTypeId: sub._id,
        minValue: formData[`minValue_${sub._id}`] || "",
        maxValue: formData[`maxValue_${sub._id}`] || "",
        value: formData[`value_${sub._id}`] || "",
        gainLossPerDay: formData[`gainLossPerDay_${sub._id}`] || ""
      }));
      const dataToSubmit = {
        name: formData.name,
        readingType: formData.readingType._id,
        readingSubType: formData.readingSubType.map((sub) => sub._id) || [],
        conditionGroup: formData.condition
          ? formData.condition.map((item) => item._id)
          : [],
        condition: analyzeByValue,
        valueProperty: subTypeValues,
        extremeReadingPatientMessage: formData.extremeReadingPatientMessage,
        extremeReadingCareProviderMessage:
          formData.extremeReadingCareProviderMessage,
        extremeReadingOneHourCareProviderMessage:
          formData.extremeReadingOneHourCareProviderMessage,
        notifyReadingOwner: formData.notifyReadingOwner,
        notifySelf: formData.notifySelf,
        sharedUsers: formData.patients.map((item) => item._id),
        notifyStaff: formData.notifyStaff
      };
      doFetchCreate({
        url: "/create-extreme-reading-expectation",
        method: "POST",
        body: dataToSubmit,
        disableWait: true,
      });
    }
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.value;

    const name = target.name;
    const notifySelf = formData.notifySelf;
    const notifyReadingOwner = formData.notifyReadingOwner;
    const notifyStaff = formData.notifyStaff;
    if (target.type === "checkbox") {
      let index;
      if (target.checked) {
        // add the numerical value of the checkbox to options array
        if (name === "notifySelf") {
          notifySelf.push(event.target.value);
        } else if (name === "notifyReadingOwner") {
          notifyReadingOwner.push(event.target.value);
        }else if(name === "notifyStaff"){
          notifyStaff.push(event.target.value);
        }
      } else {
        // or remove the value from the unchecked checkbox from the array
        if (name === "notifySelf") {
          index = notifySelf.indexOf(event.target.value);
          notifySelf.splice(index, 1);
        } else if (name === "notifyReadingOwner") {
          index = notifyReadingOwner.indexOf(event.target.value);
          notifyReadingOwner.splice(index, 1);
        }else if(name === "notifyStaff"){
          index = notifyStaff.indexOf(event.target.value);
          notifyStaff.splice(index, 1);
        }
      }
      setFormData((prev) => ({
        ...prev,
        notifySelf: notifySelf,
        notifyReadingOwner: notifyReadingOwner,
        notifyStaff: notifyStaff
      }));
      return;
    }
  };

  useEffect(() => {
    if (!isCreating && submitted) {
      setFormData({
        name: "",
        readingType: null,
        readingSubType: [],
        condition: null,
        analyzeBy: null,
        message: "",
        messageCareprovider: "",
        extremeReadingPatientMessage: "",
        extremeReadingCareProviderMessage: "",
        extremeReadingOneHourCareProviderMessage: "",
        notifyReadingOwner: [],
        notifySelf: [],
        notifyStaff: [],
        patients: [],
      });
      setFormError({});
      setSubmitted(false);
      history.push("/expectations?tab=3");
    }
  }, [isCreating]);

  const defaultMessageSet = (readingType) => {
    if (!readingType || !readingType._id) {
      return {
        extremeReadingCareProvider: "",
        extremeReadingPatient: "",
        extremeReadingOneHourCareProvider: ""
      };
    }

    if (readingType._id === "5bb306382598931ffbd1b623") {
      return ExtremeGlucoseReadingMessage;
    } else if (readingType._id === "5bb306382598931ffbd1b624") {
      return ExtremeBloodPressureReadingMessage;
    } else if (readingType._id === "5bb306382598931ffbd1b626") {
      return ExtremeOxigenReadingMessage;
    } else if (readingType._id === "5bb306382598931ffbd1b625") {
      return ExtremeWeightReadingMessage;
    }

    return {
      extremeReadingCareProvider: "",
      extremeReadingPatient: "",
      extremeReadingOneHourCareProvider: ""
    };
  };
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Link
          to="/expectations?tab=3"
          className="text-white btn btn-gradient-dark btn-icon-text"
          style={{ minWidth: 110, marginLeft: 20 }}
        >
          <i className="mdi mdi-arrow-left btn-icon-prepend" />
          Back
        </Link>
        <span
          style={{
            fontWeight: 600,
            fontSize: 22,
            color: "#222",
            flex: 1,
            textAlign: "center",
          }}
        >
         Add Event Expectation - Retest Logic
        </span>
        <div style={{ minWidth: 110 }} />
      </div>
      <div className="col-12 grid-margin">
        <div className="card mt-2">
          <div className="card-body project-tab pt-2 mt-4">
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="exampleEmail">Event Expectation Name</Label>
                <Input
                  type="text"
                  name="name"
                  id="exampleEmail"
                  className="input-date-filter"
                  placeholder="Expectation Name"
                  value={formData.name}
                  onChange={(e) => {
                    const newFormData = { ...formData, name: e.target.value };
                    setFormData(newFormData);
                    setFormError(validateForm(newFormData));
                  }}
                />
                {submitted && formError.name && (
                  <div style={{ color: "red" }}>{formError.name}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="exampleSelect">Pick a Reading Type</Label>
                <RequiredSelect
                  SelectComponent={Select}
                  getOptionLabel={(option) =>
                    option["description"] + " (" + option["abbrev"] + ")"
                  }
                  getOptionValue={(option) => option["_id"]}
                  options={
                    response && response.data.readingType
                      ? response.data.readingType
                      : []
                  }
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  value={formData.readingType}
                  onChange={(selectedOption) => {
                    const messages = defaultMessageSet(selectedOption);

                    const updatedFormData = {
                      ...formData,
                      readingType: selectedOption,
                      readingSubType: [],
                      value: "",
                      gainLossPerDay: "",
                      minValue: "",
                      maxValue: "",
                      analyzeBy: "",
                      extremeReadingCareProviderMessage: messages.extremeReadingCareProvider,
                      extremeReadingPatientMessage: messages.extremeReadingPatient,
                      extremeReadingOneHourCareProviderMessage: messages.extremeReadingOneHourCareProvider
                    };

                    setFormData(updatedFormData);
                    setFormError(
                      validateForm({ ...formData, readingType: selectedOption })
                    );
                  }}
                />
                {submitted && formError.readingType && (
                  <div style={{ color: "red" }}>{formError.readingType}</div>
                )}
              </FormGroup>
              {(formData.readingType && formData.readingType._id) !== "5bb306382598931ffbd1b625" && (<FormGroup>
                <Label for="exampleSelect">Analyze By</Label>
                <RequiredSelect
                  SelectComponent={Select}
                  options={[
                    { value: "between", label: "In Range" },
                    { value: ">=", label: "Greater Than" },
                    { value: "<=", label: "Less Than" },
                  ]}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  value={formData.analyzeBy}
                  onChange={(selectedOption) => {
                    // Remove all minValue_*, maxValue_*, value_* keys from formData
                    const cleanedFormData = Object.keys(formData).reduce(
                      (acc, key) => {
                        if (
                          !key.startsWith("minValue_") &&
                          !key.startsWith("maxValue_") &&
                          !key.startsWith("value_")
                        ) {
                          acc[key] = formData[key];
                        }
                        return acc;
                      },
                      {}
                    );
                    setFormData({
                      ...cleanedFormData,
                      analyzeBy: selectedOption,
                      value: "",
                      minValue: "",
                      maxValue: "",
                    });
                    setFormError(
                      validateForm({
                        ...cleanedFormData,
                        analyzeBy: selectedOption,
                        value: "",
                        minValue: "",
                        maxValue: "",
                      })
                    );
                  }}
                />
                {submitted && formError.analyzeBy && (
                  <div style={{ color: "red" }}>{formError.analyzeBy}</div>
                )}
              </FormGroup>)}
              <FormGroup>
                <Label for="exampleSelect">Pick a Readingsub Type</Label>
                <RequiredSelect
                  SelectComponent={Select}
                  getOptionLabel={(option) =>
                   (option["name"] === "Beats Per Minute(BPM)" ? "Heart Rate" : option["name"]) + " (" + option["abbrev"] + ")"
                  }
                  getOptionValue={(option) => option["_id"]}
                  options={
                    response &&
                      response.data.readingSubType &&
                      formData.readingType
                      ? response.data.readingSubType.filter(
                        (sub) =>
                          sub.reading ===
                          (formData.readingType._id || formData.readingType)
                      )
                      : []
                  }
                  isLoading={isLoading}
                  isDisabled={!formData.readingType}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  value={formData.readingSubType}
                  onChange={(selectedOptions) => {
                    setFormData({
                      ...formData,
                      readingSubType: selectedOptions,
                    });
                    setFormError(
                      validateForm({
                        ...formData,
                        readingSubType: selectedOptions,
                      })
                    );
                  }}
                />
                {submitted && formError.readingSubType && (
                  <div style={{ color: "red" }}>{formError.readingSubType}</div>
                )}
              </FormGroup>
              <FormGroup>
                {selectedSubTypes.map((sub, idx) => (
                  <div
                    key={sub._id || idx}
                    className="d-flex align-items-center mb-2"
                    style={{ gap: 12 }}
                  >
                    <span style={{ minWidth: 180 }}>
                      {sub.name === "Beats Per Minute(BPM)" ? "Heart Rate" : sub.name === "Body Weight" ? "Body Weight (Gain Loss Per Day)" : sub.name} ({sub.abbrev})
                    </span>
                    {(formData.readingType && formData.readingType._id) == "5bb306382598931ffbd1b625" ? (
                      <Input
                        type="number"
                        className="input-date-filter"
                        placeholder="Weight"
                        min={0}
                        value={formData[`gainLossPerDay_${sub._id}`] || ""}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, ""); // remove non-numeric characters
                          setFormData({
                            ...formData,
                            [`gainLossPerDay_${sub._id}`]: numericValue,
                          });
                        }}
                        style={{ width: 120 }}
                      />
                    ) : (
                      analyzeByValue === "between" ? (
                        <>
                          <Input
                            type="text"
                            className="input-date-filter"
                            placeholder="Min"
                            value={formData[`minValue_${sub._id}`] || ""}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(/[^0-9]/g, "");
                              setFormData({
                                ...formData,
                                [`minValue_${sub._id}`]: numericValue,
                              });
                            }}
                            style={{ width: 100 }}
                          />
                          <span style={{ margin: "0 6px" }}>to</span>
                          <Input
                            type="text"
                            className="input-date-filter"
                            placeholder="Max"
                            min={0}
                            value={formData[`maxValue_${sub._id}`] || ""}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(/[^0-9]/g, "");
                              setFormData({
                                ...formData,
                                [`maxValue_${sub._id}`]: numericValue,
                              });
                            }}
                            style={{ width: 100 }}
                          />
                        </>
                      ) : (
                        <Input
                          type="number"
                          className="input-date-filter"
                          placeholder="Value"
                          min={0}
                          value={formData[`value_${sub._id}`] || ""}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/[^0-9]/g, "");
                            setFormData({
                              ...formData,
                              [`value_${sub._id}`]: numericValue,
                            });
                          }}
                          style={{ width: 120 }}
                        />
                      )
                    )}
                    {submitted && formError[`analyzeValue_${sub._id}`] && (
                      <div style={{ color: "red" }}>
                        {formError[`analyzeValue_${sub._id}`]}
                      </div>
                    )}
                  </div>
                ))}
              </FormGroup>
              <>
                <FormGroup>
                  <Label for="exampleSelect">
                    Select a Condition to Monitor
                  </Label>
                  <RequiredSelect
                    SelectComponent={Select}
                    options={
                      response && response.data && response.data.condition
                        ? response.data.condition
                        : []
                    }
                    isMulti={true}
                    getOptionLabel={(option) =>
                      `${option["condition"]}  (${option["description"]})`
                    }
                    getOptionValue={(option) => option["_id"]}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    value={formData.condition}
                    onChange={(selectedOption) => {
                      setFormData({ ...formData, condition: selectedOption });
                      setFormError(
                        validateForm({ ...formData, condition: selectedOption })
                      );
                    }}
                  />
                  {submitted && formError.condition && (
                    <div style={{ color: "red" }}>{formError.condition}</div>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label for="exampleSelect">Select a Patient to Monitor</Label>
                  <Select
                    options={
                      response && response.data && response.data.patients
                        ? response.data.patients
                        : []
                    }
                    isMulti={true}
                    getOptionLabel={(option) =>
                      option["fname"] + " " + option["lname"]
                    }
                    getOptionValue={(option) => option}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    onChange={(selectedOption) => {
                      setFormData({ ...formData, patients: selectedOption });
                      setFormError(
                        validateForm({ ...formData, patients: selectedOption })
                      );
                    }}
                    value={formData.patients || []}
                  />
                  {submitted && formError.patients && (
                    <div style={{ color: "red" }}>{formError.patients}</div>
                  )}
                </FormGroup>
              </>
              {/* ...rest of your form... */}
              <FormGroup>
                <Label for="exampleText">
                  Extreme Notification Message (Patient)
                </Label>
                <Input
                  type="textarea"
                  name="extremeReadingPatientMessage"
                  className="input-date-filter"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extremeReadingPatientMessage: e.target.value,
                    })
                  }
                  rows={10}
                  value={formData.extremeReadingPatientMessage}
                />
                {submitted && formError.extremeReadingPatientMessage && (
                  <div style={{ color: "red" }}>
                    {formError.extremeReadingPatientMessage}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="exampleText">
                  If patient retest and still reading extreme message for care provider
                </Label>
                <Input
                  type="textarea"
                  name="extremeReadingCareProviderMessage"
                  className="input-date-filter"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extremeReadingCareProviderMessage: e.target.value,
                    })
                  }
                  rows={10}
                  value={formData.extremeReadingCareProviderMessage}
                />
                {submitted && formError.extremeReadingCareProviderMessage && (
                  <div style={{ color: "red" }}>
                    {formError.extremeReadingCareProviderMessage}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="exampleText">
                  If patient not taking any reading in one hour then notify message for care provider
                </Label>
                <Input
                  type="textarea"
                  name="extremeReadingOneHourCareProviderMessage"
                  className="input-date-filter"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extremeReadingOneHourCareProviderMessage: e.target.value,
                    })
                  }
                  rows={10}
                  value={formData.extremeReadingOneHourCareProviderMessage}
                />
                {submitted &&
                  formError.extremeReadingOneHourCareProviderMessage && (
                    <div style={{ color: "red" }}>
                      {formError.extremeReadingOneHourCareProviderMessage}
                    </div>
                  )}
              </FormGroup>

              <FormGroup>
                <Label for="exampleCheckbox">Notify Me</Label>
                <div>
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline"
                    name="notifySelf"
                    value="inbox"
                    label="INBOX"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline2"
                    name="notifySelf"
                    label="SMS"
                    value="sms"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline3"
                    name="notifySelf"
                    label="VOICE"
                    value="voice"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline4"
                    name="notifySelf"
                    label="EMAIL"
                    value="email"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                </div>
                {submitted && formError.notifySelf && (
                  <div style={{ color: "red" }}>{formError.notifySelf}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="exampleCheckbox">Notify Staff</Label>
                <div>
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline10"
                    name="notifyStaff"
                    value="inbox"
                    label="INBOX"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline11"
                    name="notifyStaff"
                    label="SMS"
                    value="sms"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline12"
                    name="notifyStaff"
                    label="VOICE"
                    value="voice"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline13"
                    name="notifyStaff"
                    label="EMAIL"
                    value="email"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                </div>
                {submitted && formError.notifyStaff && (
                  <div style={{ color: "red" }}>
                    {formError.notifyStaff}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="exampleCheckbox">Notify Readings Owner</Label>
                <div>
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline6"
                    name="notifyReadingOwner"
                    value="inbox"
                    label="INBOX"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline7"
                    name="notifyReadingOwner"
                    label="SMS"
                    value="sms"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline8"
                    name="notifyReadingOwner"
                    label="VOICE"
                    value="voice"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                  <CustomInput
                    type="checkbox"
                    id="exampleCustomInline9"
                    name="notifyReadingOwner"
                    label="EMAIL"
                    value="email"
                    inline
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                  />
                </div>
                {submitted && formError.notifyReadingOwner && (
                  <div style={{ color: "red" }}>
                    {formError.notifyReadingOwner}
                  </div>
                )}
              </FormGroup>
              <Button
                type="submit"
                className="btn btn-info btn-lg btn-block px-10"
                disabled={isCreating || isLoading}
              >
                {isCreating ? (
                  <i className="mdi mdi-loading mdi-spin mr-2" />
                ) : (
                  <i className="mdi  mdi-content-save" />
                )}{" "}
                Submit
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRouter(AddExtremeReading);
